import React, { useMemo, useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { ModalEditarTarea } from './ModalEditarTarea';
import { EmptyState } from './EmptyState';
import { TareaConRelaciones, EstadoTarea } from '../types';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];
const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const estadoColor: Record<EstadoTarea, string> = {
    backlog: 'bg-slate-400',
    por_hacer: 'bg-blue-500',
    en_progreso: 'bg-amber-500',
    esperando_cliente: 'bg-orange-500',
    testing: 'bg-purple-500',
    produccion: 'bg-emerald-500'
};

const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

const formatYMD = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const isOverdueDate = (fecha: string | null, estado: EstadoTarea): boolean => {
    if (!fecha || estado === 'produccion') return false;
    const today = new Date(); today.setHours(0,0,0,0);
    return new Date(fecha) < today;
};

export const VistaCalendario: React.FC = () => {
    const { swimlanes, actualizarTarea, eliminarTarea, marcarCompletado } = useKanban();
    const [cursor, setCursor] = useState<Date>(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [selectedDay, setSelectedDay] = useState<Date | null>(null);
    const [selectedTarea, setSelectedTarea] = useState<TareaConRelaciones | null>(null);

    const tareasFlat: TareaConRelaciones[] = useMemo(() => {
        const all: TareaConRelaciones[] = [];
        swimlanes.forEach(sw => {
            Object.values(sw.tareas).forEach(arr => arr.forEach(t => all.push(t)));
        });
        return all;
    }, [swimlanes]);

    const tareasPorFecha = useMemo(() => {
        const map = new Map<string, TareaConRelaciones[]>();
        tareasFlat.forEach(t => {
            if (!t.fecha_limite) return;
            const key = formatYMD(new Date(t.fecha_limite));
            const arr = map.get(key) || [];
            arr.push(t);
            map.set(key, arr);
        });
        return map;
    }, [tareasFlat]);

    const cells = useMemo(() => {
        const first = startOfMonth(cursor);
        const lastDay = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
        const firstWeekday = (first.getDay() + 6) % 7; // lunes = 0
        const totalCells = Math.ceil((firstWeekday + lastDay) / 7) * 7;
        const result: Array<{ date: Date; inMonth: boolean }> = [];
        for (let i = 0; i < totalCells; i++) {
            const dayOffset = i - firstWeekday;
            const d = new Date(cursor.getFullYear(), cursor.getMonth(), dayOffset + 1);
            result.push({ date: d, inMonth: d.getMonth() === cursor.getMonth() });
        }
        return result;
    }, [cursor]);

    const tareasDelMes = useMemo(() => {
        return tareasFlat.filter(t => {
            if (!t.fecha_limite) return false;
            const d = new Date(t.fecha_limite);
            return d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth();
        });
    }, [tareasFlat, cursor]);

    const navigate = (delta: number) => {
        setCursor(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };
    const goToday = () => {
        const t = new Date();
        setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    };

    const today = new Date();
    const tareasDelDia = selectedDay
        ? (tareasPorFecha.get(formatYMD(selectedDay)) || [])
        : [];

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg"
                        title="Mes anterior"
                    >
                        ◀
                    </button>
                    <h2 className="text-lg font-semibold text-slate-800 min-w-[180px] text-center">
                        {MESES[cursor.getMonth()]} {cursor.getFullYear()}
                    </h2>
                    <button
                        onClick={() => navigate(1)}
                        className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg"
                        title="Mes siguiente"
                    >
                        ▶
                    </button>
                    <button
                        onClick={goToday}
                        className="ml-2 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                    >
                        Hoy
                    </button>
                </div>
                <div className="text-xs text-slate-500">
                    {tareasDelMes.length} tarea{tareasDelMes.length !== 1 ? 's' : ''} con fecha límite este mes
                </div>
            </div>

            {/* Desktop/tablet: grilla de 7 columnas. Comprimir 7 columnas en un
                celular angosto nunca se lee bien de verdad, así que abajo hay
                una lista de agenda aparte para sm:hidden en vez de forzar la
                misma grilla más chica. */}
            <div className="hidden sm:grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {DIAS.map(d => (
                    <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        {d}
                    </div>
                ))}
            </div>

            <div className="hidden sm:grid grid-cols-7">
                {cells.map(({ date, inMonth }, idx) => {
                    const key = formatYMD(date);
                    const tareas = tareasPorFecha.get(key) || [];
                    const isToday = sameDay(date, today);
                    const overdueCount = tareas.filter(t => isOverdueDate(t.fecha_limite, t.estado)).length;

                    return (
                        <button
                            key={idx}
                            onClick={() => tareas.length > 0 && setSelectedDay(date)}
                            disabled={tareas.length === 0}
                            className={`relative min-h-[88px] p-1.5 border-r border-b border-slate-100 text-left transition-colors ${
                                !inMonth ? 'bg-slate-50/50' : 'bg-white'
                            } ${tareas.length > 0 ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default'} ${
                                isToday ? 'ring-2 ring-inset ring-blue-400' : ''
                            }`}
                        >
                            <div className={`text-xs font-semibold mb-1 ${
                                isToday ? 'text-blue-700' :
                                !inMonth ? 'text-slate-300' : 'text-slate-700'
                            }`}>
                                {date.getDate()}
                            </div>
                            <div className="space-y-0.5">
                                {tareas.slice(0, 3).map(t => (
                                    <div
                                        key={t.id}
                                        className={`text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-1 ${
                                            isOverdueDate(t.fecha_limite, t.estado)
                                                ? 'bg-red-100 text-red-700 font-medium'
                                                : 'bg-slate-100 text-slate-700'
                                        }`}
                                        title={t.titulo}
                                    >
                                        <span
                                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: t.cliente_color }}
                                        />
                                        <span className="truncate">{t.titulo}</span>
                                    </div>
                                ))}
                                {tareas.length > 3 && (
                                    <div className="text-[10px] text-slate-500 px-1.5 font-medium">
                                        +{tareas.length - 3} más
                                    </div>
                                )}
                                {overdueCount > 0 && (
                                    <div className="text-[10px] text-red-600 px-1.5 font-semibold">
                                        ⚠ {overdueCount} vencida{overdueCount > 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Mobile: agenda en lista — solo días del mes con tareas, en
                vez de la grilla completa. Mismo comportamiento al tocar un
                día (abre el mismo detalle que en desktop). */}
            <div className="sm:hidden divide-y divide-slate-100">
                {cells.filter(c => c.inMonth && (tareasPorFecha.get(formatYMD(c.date))?.length ?? 0) > 0).length === 0 ? (
                    <EmptyState
                        icon="📅"
                        title="Sin tareas este mes"
                        description="No hay tareas con fecha límite en este mes."
                        variant="compact"
                    />
                ) : (
                    cells.filter(c => c.inMonth && (tareasPorFecha.get(formatYMD(c.date))?.length ?? 0) > 0).map(({ date }) => {
                        const key = formatYMD(date);
                        const tareas = tareasPorFecha.get(key) || [];
                        const isToday = sameDay(date, today);
                        const overdueCount = tareas.filter(t => isOverdueDate(t.fecha_limite, t.estado)).length;
                        return (
                            <button
                                key={key}
                                onClick={() => setSelectedDay(date)}
                                className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-blue-50 transition-colors"
                            >
                                <div className={`flex flex-col items-center justify-center w-11 h-11 rounded-lg flex-shrink-0 ${
                                    isToday ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
                                }`}>
                                    <span className="text-[10px] uppercase leading-none">{DIAS[(date.getDay() + 6) % 7]}</span>
                                    <span className="text-sm font-bold leading-none mt-0.5">{date.getDate()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-slate-500 mb-1">
                                        {tareas.length} tarea{tareas.length !== 1 ? 's' : ''}
                                        {overdueCount > 0 && <span className="text-red-600 font-semibold"> · ⚠ {overdueCount} vencida{overdueCount > 1 ? 's' : ''}</span>}
                                    </div>
                                    <div className="space-y-1">
                                        {tareas.slice(0, 2).map(t => (
                                            <div
                                                key={t.id}
                                                className={`text-xs truncate flex items-center gap-1.5 ${
                                                    isOverdueDate(t.fecha_limite, t.estado) ? 'text-red-700 font-medium' : 'text-slate-700'
                                                }`}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.cliente_color }} />
                                                <span className="truncate">{t.titulo}</span>
                                            </div>
                                        ))}
                                        {tareas.length > 2 && (
                                            <div className="text-[11px] text-slate-500">+{tareas.length - 2} más</div>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>

            {selectedDay && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedDay(null)}>
                    <div
                        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
                            <div>
                                <h3 className="text-base font-semibold text-slate-800">
                                    {DIAS[(selectedDay.getDay() + 6) % 7]} {selectedDay.getDate()} de {MESES[selectedDay.getMonth()]}, {selectedDay.getFullYear()}
                                </h3>
                                <p className="text-xs text-slate-500">
                                    {tareasDelDia.length} tarea{tareasDelDia.length !== 1 ? 's' : ''} con fecha límite
                                </p>
                            </div>
                            <button onClick={() => setSelectedDay(null)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
                        </div>
                        <div className="overflow-y-auto p-3 space-y-2">
                            {tareasDelDia.length === 0 && (
                                <EmptyState
                                    icon="📅"
                                    title="Sin tareas este día"
                                    description="No hay tareas con fecha límite en este día. Hacé click en otro día del calendario."
                                    variant="compact"
                                />
                            )}
                            {tareasDelDia.map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => { setSelectedTarea(t); setSelectedDay(null); }}
                                    className="w-full text-left px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                                >
                                    <div className="flex items-start gap-2">
                                        <span
                                            className="mt-1 w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: t.cliente_color }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm text-slate-900 line-clamp-1">{t.titulo}</div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] text-slate-500">{t.empresa}</span>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded text-white ${estadoColor[t.estado]}`}>
                                                    {t.estado.replace('_', ' ')}
                                                </span>
                                                {isOverdueDate(t.fecha_limite, t.estado) && (
                                                    <span className="text-[10px] text-red-600 font-semibold">⚠ Vencida</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {selectedTarea && (
                <ModalEditarTarea
                    tarea={selectedTarea}
                    onClose={() => setSelectedTarea(null)}
                    onActualizar={actualizarTarea}
                    onEliminar={eliminarTarea}
                    onMarcarCompletado={marcarCompletado}
                />
            )}
        </div>
    );
};